import {
  CareerSiteFiltersOption,
  CareerSiteSettings,
} from 'api-clients/monolith';
import {
  AppContext,
  FILTER_PILL_FIELDS,
  getDefaultActiveFilters,
} from 'contexts/v2/app-context';
import { SlidersHorizontal } from 'lucide-react';
import React, { useCallback, useContext, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { FiltersContent } from 'components/filters/index';
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from 'components/ui/sheet';

interface MobileFiltersDrawerProps {
  brandSetting: CareerSiteSettings;
  filterOptions?: {
    jobTypes: CareerSiteFiltersOption[];
    jobHours: CareerSiteFiltersOption[];
    countries: CareerSiteFiltersOption[];
    states: CareerSiteFiltersOption[];
    cities: CareerSiteFiltersOption[];
    category: CareerSiteFiltersOption[];
    position: CareerSiteFiltersOption[];
    locationName: CareerSiteFiltersOption[];
  };
}

export function MobileFiltersDrawer({
  brandSetting,
  filterOptions,
}: MobileFiltersDrawerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedCounts, setExpandedCounts] = useState<Record<string, number>>(
    {},
  );

  const {
    activeFiltersV2,
    setActiveFiltersV2,
    pendingFiltersV2,
    setPendingFiltersV2,
    updateUrlWithParams,
    accountSlug,
    brandId,
    setIsFiltersDrawerOpen,
  } = useContext(AppContext);

  const handleOpen = useCallback(() => {
    setPendingFiltersV2({ ...activeFiltersV2 });
    setIsFiltersDrawerOpen(true);
    setIsOpen(true);
  }, [activeFiltersV2, setPendingFiltersV2, setIsFiltersDrawerOpen]);

  const hasFilterChanges = useCallback(() => {
    return FILTER_PILL_FIELDS.some(key => {
      const activeKeys = (activeFiltersV2[key] ?? []).map(v => v.key).sort();
      const pendingKeys = (pendingFiltersV2[key] ?? []).map(v => v.key).sort();
      if (activeKeys.length !== pendingKeys.length) return true;
      return activeKeys.some((k, i) => k !== pendingKeys[i]);
    });
  }, [activeFiltersV2, pendingFiltersV2]);

  const handleApply = useCallback(() => {
    if (hasFilterChanges()) {
      setActiveFiltersV2({ ...pendingFiltersV2 });

      if (accountSlug) {
        FILTER_PILL_FIELDS.forEach(key => {
          const values = pendingFiltersV2[key] ?? [];
          const urlValues = values.map(v => v.key);
          updateUrlWithParams(accountSlug, brandId, key, urlValues);
        });
      }
    }

    setIsFiltersDrawerOpen(false);
    setIsOpen(false);
  }, [
    accountSlug,
    brandId,
    hasFilterChanges,
    pendingFiltersV2,
    setActiveFiltersV2,
    setIsFiltersDrawerOpen,
    updateUrlWithParams,
  ]);

  const handleCancel = useCallback(() => {
    setIsFiltersDrawerOpen(false);
    setIsOpen(false);
  }, [setIsFiltersDrawerOpen]);

  const handleClear = useCallback(() => {
    setPendingFiltersV2(getDefaultActiveFilters());
  }, [setPendingFiltersV2]);

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={handleOpen}
        className="text-primary-600 flex items-center gap-1.5 text-sm font-medium"
        data-testid="mobile-filters-button"
      >
        <SlidersHorizontal className="h-4 w-4" />
        <FormattedMessage
          defaultMessage="Filters"
          description="Button to open filters drawer on mobile"
        />
      </button>

      {/* Drawer Content */}
      <SheetContent open={isOpen} onClose={handleApply} side="left">
        <SheetHeader>
          <div className="flex items-center gap-3">
            <SheetTitle>
              <FormattedMessage
                defaultMessage="Filters"
                description="Title for filters drawer"
              />
            </SheetTitle>
            <button
              type="button"
              onClick={handleClear}
              className="text-primary-600 text-sm font-medium"
              data-testid="mobile-filters-clear"
            >
              <FormattedMessage
                defaultMessage="Clear"
                description="Button to clear all filters in drawer"
              />
            </button>
          </div>
          <SheetClose onClose={handleApply} />
        </SheetHeader>

        <SheetBody className="p-0">
          <FiltersContent
            brandSetting={brandSetting}
            filterOptions={filterOptions}
            usePendingFilters
            expandedCounts={expandedCounts}
            onExpandedCountsChange={setExpandedCounts}
          />
        </SheetBody>

        <SheetFooter>
          <div className="flex w-full gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-3 text-base font-medium text-gray-700 transition-colors hover:bg-gray-50"
              data-testid="mobile-filters-cancel"
            >
              <FormattedMessage
                defaultMessage="Cancel"
                description="Button to cancel and restore original filters"
              />
            </button>
            <button
              type="button"
              onClick={handleApply}
              className="bg-primary-600 hover:bg-primary-700 flex-1 rounded-lg px-4 py-3 text-base font-medium text-white transition-colors"
              data-testid="mobile-filters-apply"
            >
              <FormattedMessage
                defaultMessage="Apply"
                description="Button to apply filters"
              />
            </button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
